import React, { useEffect, useMemo, useState } from 'react';
import { productsAPI, vendorsAPI, categoriesAPI } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

function productLicenses(product) {
  if (Array.isArray(product?.licenses) && product.licenses.length > 0) {
    return product.licenses.filter((item) => item?.name);
  }
  if (Array.isArray(product?.calculator_config?.licenses) && product.calculator_config.licenses.length > 0) {
    return product.calculator_config.licenses.filter((item) => item?.name);
  }
  return [{ name: 'Базовая лицензия', price: product?.price || 'по запросу', description: '' }];
}

function formatLicensePrice(price) {
  if (!price || String(price).toLowerCase().includes('по запросу')) return 'по запросу';
  return String(price);
}

const CabinetCatalogPicker = ({ open, onOpenChange, onResolved, resolveCatalog }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetail, setProductDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedLicenseName, setSelectedLicenseName] = useState('');
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setSelectedProduct(null);
    setProductDetail(null);
    setSelectedLicenseName('');
    (async () => {
      try {
        const [vendorRows, categoryRows] = await Promise.all([
          vendorsAPI.getVendors({ view: 'catalog' }).catch(() => []),
          categoriesAPI.getCategories().catch(() => []),
        ]);
        if (cancelled) return;
        setVendors(Array.isArray(vendorRows) ? vendorRows : []);
        const cats = Array.isArray(categoryRows) ? categoryRows : [];
        setCategories(cats.filter((item) => item.id && item.id !== 'all'));
      } catch (error) {
        if (!cancelled) {
          toast({ title: 'Не удалось загрузить каталог', description: error.message, variant: 'destructive' });
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const vendorSlug =
      vendorFilter === 'all'
        ? undefined
        : (vendors.find((v) => v.name === vendorFilter)?.slug || undefined);

    productsAPI
      .getProducts({
        view: 'catalog',
        limit: 24,
        offset: 0,
        q: debouncedQ || undefined,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        vendor_slug: vendorSlug,
      })
      .then((page) => {
        if (cancelled) return;
        const items = Array.isArray(page) ? page : page?.items || [];
        setProducts(items);
      })
      .catch((error) => {
        if (!cancelled) {
          setProducts([]);
          toast({ title: 'Не удалось загрузить каталог', description: error.message, variant: 'destructive' });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debouncedQ, categoryFilter, vendorFilter, vendors]);

  const licenses = useMemo(
    () => (productDetail ? productLicenses(productDetail) : []),
    [productDetail]
  );

  const selectProduct = async (product) => {
    setSelectedProduct(product);
    setSelectedLicenseName('');
    setDetailLoading(true);
    try {
      const detail = await productsAPI.getProduct(product.id);
      setProductDetail(detail);
      const list = productLicenses(detail);
      if (list.length === 1) setSelectedLicenseName(list[0].name);
    } catch (error) {
      toast({ title: 'Не удалось открыть решение', description: error.message, variant: 'destructive' });
      setProductDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const confirm = async () => {
    if (!selectedProduct || !selectedLicenseName) {
      toast({ title: 'Выберите решение и тариф', variant: 'destructive' });
      return;
    }
    setConfirming(true);
    try {
      const resolved = await resolveCatalog({
        product_id: selectedProduct.id,
        license_name: selectedLicenseName,
      });
      onResolved(resolved);
      onOpenChange(false);
    } catch (error) {
      toast({ title: 'Не удалось добавить из каталога', description: error.message, variant: 'destructive' });
    } finally {
      setConfirming(false);
    }
  };

  const vendorOptions = useMemo(() => {
    const names = new Set();
    vendors.forEach((item) => {
      if (item?.name) names.add(item.name);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'ru'));
  }, [vendors]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Каталог решений</DialogTitle>
          <DialogDescription>
            Найдите решение и тариф — они подставятся в форму лицензии. Можно также выбрать сервис вручную в форме.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 md:grid-cols-3">
          <div className="space-y-2 md:col-span-3">
            <Label>Поиск</Label>
            <Input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Название, вендор…"
            />
          </div>
          <div className="space-y-2">
            <Label>Категория</Label>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все</SelectItem>
                {categories.map((item) => (
                  <SelectItem key={item.id} value={item.id}>{item.name || item.id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Вендор</Label>
            <Select value={vendorFilter} onValueChange={setVendorFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все</SelectItem>
                {vendorOptions.map((name) => (
                  <SelectItem key={name} value={name}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-2 grid max-h-56 gap-2 overflow-y-auto sm:grid-cols-2">
          {loading && <p className="text-sm text-muted-foreground">Загрузка каталога…</p>}
          {!loading && products.length === 0 && (
            <p className="text-sm text-muted-foreground">Ничего не найдено</p>
          )}
          {!loading && products.map((product) => (
            <button
              key={product.id}
              type="button"
              className={`rounded-md border p-3 text-left text-sm transition-colors hover:bg-muted/50 ${
                selectedProduct?.id === product.id ? 'border-primary bg-muted/40' : ''
              }`}
              onClick={() => selectProduct(product)}
            >
              <div className="font-medium">{product.name}</div>
              <div className="text-muted-foreground">{product.vendor || '—'}</div>
            </button>
          ))}
        </div>

        {selectedProduct && (
          <div className="space-y-2 border-t pt-3">
            <Label>Тариф лицензии: {selectedProduct.name}</Label>
            {detailLoading && <p className="text-sm text-muted-foreground">Загрузка тарифов…</p>}
            {!detailLoading && (
              <div className="space-y-2">
                {licenses.map((license) => (
                  <label
                    key={license.name}
                    className={`flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm ${
                      selectedLicenseName === license.name ? 'border-primary bg-muted/40' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="cabinet-catalog-license"
                      className="mt-1"
                      checked={selectedLicenseName === license.name}
                      onChange={() => setSelectedLicenseName(license.name)}
                    />
                    <span>
                      <span className="font-medium">{license.name}</span>
                      <span className="ml-2 text-muted-foreground">{formatLicensePrice(license.price)}</span>
                      {license.description ? (
                        <span className="mt-1 block text-muted-foreground">{license.description}</span>
                      ) : null}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button
            type="button"
            disabled={!selectedProduct || !selectedLicenseName || confirming}
            onClick={confirm}
          >
            {confirming ? 'Добавление…' : 'Добавить в управление'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CabinetCatalogPicker;
